import type { ContentCollection, SourceConfig } from '../../shared/contracts'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/services/api'
import { collectionIdFromUrl } from '@/utils/contentSources'
import { MAX_ENABLED_SOURCES, normalizeCollection, normalizeCollections } from '../../shared/content-sources'
import { useAppStore } from './app'

const LEGACY_STORAGE_KEY = 'cineflow:content-collections:v2'

export const useContentSourcesStore = defineStore('content-sources', () => {
    try { localStorage.removeItem(LEGACY_STORAGE_KEY) }
    catch { /* Storage may be unavailable in privacy-restricted browsers. */ }
    const app = useAppStore()
    const collections = ref<ContentCollection[]>([])
    const sources = computed<SourceConfig[]>(() => collections.value.filter(item => item.enabled).flatMap(item => item.vodSources.filter(source => source.enabled)))
    const enabledCount = computed(() => new Set([
        ...app.activeSources.map(source => source.id),
        ...sources.value.map(source => source.id),
    ]).size)
    let initializePromise: Promise<void> | undefined
    let initializePromiseKey = ''
    let initializeGeneration = 0
    let initializedKey = ''

    function enforceCombinedCapacity(items: ContentCollection[]): ContentCollection[] {
        const enabledIds = new Set(app.activeSources.map(source => source.id))
        return items.map(collection => ({
            ...collection,
            vodSources: collection.vodSources.map((source) => {
                if (!collection.enabled || !source.enabled || enabledIds.has(source.id)) { return source }
                const enabled = enabledIds.size < MAX_ENABLED_SOURCES
                if (enabled) { enabledIds.add(source.id) }
                return { ...source, enabled }
            }),
        }))
    }

    function initialize(force = false) {
        const key = `${app.user?.id || 'guest'}:${app.database}`
        if (!force && initializedKey === key) { return Promise.resolve() }
        if (initializePromise && initializePromiseKey === key) { return initializePromise }
        const generation = ++initializeGeneration
        collections.value = []
        const task = (async () => {
            try {
                if (!app.authenticated || !app.database) { return }
                const remote = await api.collections()
                if (generation === initializeGeneration) {
                    collections.value = enforceCombinedCapacity(normalizeCollections(remote.collections))
                }
            }
            finally {
                if (generation === initializeGeneration) { initializedKey = key }
            }
        })()
        initializePromise = task
        initializePromiseKey = key
        return task.finally(() => {
            if (initializePromise === task) {
                initializePromise = undefined
                initializePromiseKey = ''
            }
        })
    }

    async function importSource(payload: { id?: string, name: string, url: string }) {
        if (!app.authenticated || !app.database) { throw new Error('内容来源需要登录并绑定 D1 数据库后才能保存') }
        const request = { ...payload, id: payload.id || collectionIdFromUrl(payload.url) }
        const { collection: rawCollection } = await api.importCollection(request)
        const normalized = normalizeCollection(rawCollection)
        if (!normalized) { throw new Error('内容配置格式无效') }
        const previous = collections.value.find(item => item.id === normalized.id)
        const previousState = new Map(previous?.vodSources.map(source => [source.id, source.enabled]) || [])
        normalized.vodSources.forEach((source) => {
            if (previousState.has(source.id)) { source.enabled = previousState.get(source.id) ?? true }
        })
        const updatedCollections = enforceCombinedCapacity([normalized, ...collections.value.filter(item => item.id !== normalized.id)])
        const collection = updatedCollections[0]
        if (!collection) { throw new Error('内容配置格式无效') }
        collections.value = updatedCollections
        return collection
    }

    async function remove(id: string) {
        await api.deleteCollection(id)
        collections.value = collections.value.filter(item => item.id !== id)
    }

    async function refreshAll() {
        const results = []
        // Sequential cloud refreshes keep the global enabled-source cap deterministic.
        for (const item of collections.value) {
            try {
                const payload = { id: item.id, name: item.name, url: item.url }
                const response = await api.importCollection(payload)
                const collection = normalizeCollection(response.collection)
                if (!collection) { throw new Error('内容配置格式无效') }
                const previousState = new Map(item.vodSources.map(source => [source.id, source.enabled]))
                collection.vodSources.forEach((source) => {
                    if (previousState.has(source.id)) { source.enabled = previousState.get(source.id) ?? true }
                })
                results.push({ status: 'fulfilled' as const, value: collection })
            }
            catch (reason) {
                results.push({ status: 'rejected' as const, reason })
            }
        }
        const refreshed = new Map(results.flatMap(result => result.status === 'fulfilled' ? [[result.value.id, result.value] as const] : []))
        collections.value = enforceCombinedCapacity(collections.value.map(item => refreshed.get(item.id) || item))

        const failed = results.filter(result => result.status === 'rejected').length
        if (failed) { throw new Error(failed === results.length ? '内容来源刷新失败' : `${failed} 个内容来源暂时无法刷新`) }
    }

    function findSource(id: string) {
        return sources.value.find(source => source.id === id)
    }

    async function setSourceEnabled(collectionId: string, sourceId: string, enabled: boolean) {
        const collection = collections.value.find(item => item.id === collectionId)
        const source = collection?.vodSources.find(item => item.id === sourceId)
        if (!collection || !source) { throw new Error('点播来源不存在') }
        const alreadyRepresented = app.activeSources.some(item => item.id === source.id)
            || collections.value.some(item => item.enabled && item.vodSources.some(candidate => candidate !== source && candidate.enabled && candidate.id === source.id))
        if (enabled && !source.enabled && !alreadyRepresented && enabledCount.value >= MAX_ENABLED_SOURCES) {
            throw new Error(`最多同时启用 ${MAX_ENABLED_SOURCES} 个点播来源`)
        }
        const response = await api.setCollectionSourceEnabled(collectionId, sourceId, enabled)
        const updated = normalizeCollection(response.collection)
        if (!updated) { throw new Error('内容来源状态无效') }
        collections.value = collections.value.map(item => item.id === collectionId ? updated : item)
    }

    function clear() {
        initializeGeneration += 1
        initializePromise = undefined
        initializePromiseKey = ''
        collections.value = []
        initializedKey = ''
    }

    return { collections, sources, enabledCount, initialize, importSource, refreshAll, remove, findSource, setSourceEnabled, clear }
})
